import {
  ComponentPropsWithoutRef,
  CSSProperties,
  memo,
  ReactElement,
  useRef,
  useMemo,
  useState,
} from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { IconButton } from '@ui/buttons/icon-button';
import { CloseIcon } from '@ui/icons/material/Close';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { CheckCircleIcon } from '@ui/icons/material/CheckCircle';
import {
  UploadedFile,
  UploadedFileFromEntry,
} from '@ui/utils/files/uploaded-file';
import { useFileUploadStore } from '@common/uploads/uploader/file-upload-provider';
import { Trans } from '@ui/i18n/trans';
import { MixedText } from '@ui/i18n/mixed-text';
import { Tooltip } from '@ui/tooltip/tooltip';
import { ErrorIcon } from '@ui/icons/material/Error';
import { WarningIcon } from '@ui/icons/material/Warning';
import { message } from '@ui/i18n/message';
import { FileTypeIcon } from '@common/uploads/components/file-type-icon/file-type-icon';
import { prettyBytes } from '@ui/utils/files/pretty-bytes';
import { RefreshIcon } from '@ui/icons/material/Refresh';

interface UploadQueueProps {
  file: UploadedFile | UploadedFileFromEntry;
  style: CSSProperties;
}
export const UploadQueueItem = memo(({ file, style }: UploadQueueProps) => {
  return (
    <div
      className="absolute left-0 top-0 flex w-full items-center gap-14 p-10"
      style={style}
    >
      <div className="shrink-0 rounded border p-8">
        <FileTypeIcon className="h-22 w-22" mime={file.mime} />
      </div>
      <div className="min-w-0 flex-auto pr-10">
        <div className="mb-2 flex min-w-0 items-center gap-10">
          <div className="min-w-0 flex-auto overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
            {file.name}
          </div>
        </div>
        <SizeInfo file={file} />
      </div>
      <div className="mr-10">
        <FileStatus file={file} />
      </div>
    </div>
  );
});

interface SizeInfoProps {
  file: UploadedFile | UploadedFileFromEntry;
}
function SizeInfo({ file }: SizeInfoProps) {
  const fileUpload = useFileUploadStore(s => s.fileUploads.get(file.id));
  const bytesUploaded = fileUpload?.bytesUploaded || 0;
  const startedAt = fileUpload?.startedAt;
  const now = Date.now();

  const totalBytes = useMemo(() => prettyBytes(file.size), [file.size]);
  const uploadedBytes = useMemo(
    () => prettyBytes(bytesUploaded),
    [bytesUploaded],
  );

  let etaSeconds = 0;
  if (startedAt && bytesUploaded > 0) {
    const timeElapsed = (now - startedAt) / 1000; // seconds
    const speed = bytesUploaded / timeElapsed; // bytes per second
    const remainingBytes = file.size - bytesUploaded;
    etaSeconds = speed > 0 ? Math.ceil(remainingBytes / speed) : 0;
  }

  const formatETA = (seconds: number) => {
    if (seconds <= 0) return 'Encrypting...';
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    if (min > 0) {
      return `${min}m ${sec}s left`;
    }
    return `${sec}s left`;
  };

  let statusMessage: ReactElement;
  if (fileUpload?.status === 'completed') {
    statusMessage = <Trans message="Upload completed" />;
  } else if (fileUpload?.status === 'aborted') {
    statusMessage = <Trans message="Upload cancelled" />;
  } else if (fileUpload?.status === 'failed') {
    statusMessage = <Trans message="Upload failed" />;
  } else {
    statusMessage = (
      <Trans
        message=":bytesUploaded of :totalBytes (:eta)"
        values={{
          bytesUploaded: uploadedBytes,
          totalBytes,
          eta: formatETA(etaSeconds),
        }}
      />
    );
  }

  return <div className="text-xs text-muted">{statusMessage}</div>;
}

interface FileStatusProps {
  file: UploadedFile | UploadedFileFromEntry;
}
function FileStatus({ file }: FileStatusProps) {
  const fileUpload = useFileUploadStore(s => s.fileUploads.get(file.id));
  const abortUpload = useFileUploadStore(s => s.abortUpload);
  const retryUpload = useFileUploadStore(s => s.retryUpload);
  const percentage = fileUpload?.percentage || 0;
  const status = fileUpload?.status;
  const errorMessage = fileUpload?.errorMessage;

  const [isHovered, setIsHovered] = useState(false);
  const skipNextAbortClick = useRef(false);
  const shouldShowAbortButton = status === 'inProgress' && isHovered;

  const handleAbortWithWarning = () => {
    if (skipNextAbortClick.current) {
      skipNextAbortClick.current = false;
      return;
    }

    abortUpload(file.id);
    setIsHovered(false);
  };

  const abortButton = (
    <IconButton
      size="xs"
      iconSize="sm"
      onClick={() => {
        handleAbortWithWarning();
      }}
    >
      <CloseIcon />
    </IconButton>
  );

  const progressButton = (
    <ProgressCircle
      aria-label="Upload progress"
      size="w-24 h-24"
      value={percentage}
      trackWidth={3}
    />
  );

  let statusButton: ReactElement;
  if (status === 'failed') {
    const errMessage =
      errorMessage || message('This file could not be uploaded');
    statusButton = (
      <div style={{display: 'flex', alignItems: 'center'}}>
        <AnimatedStatus>
          <Tooltip variant="danger" label={<MixedText value={errMessage} />}>
            <ErrorIcon className="text-danger" size="md" />
          </Tooltip>
        </AnimatedStatus>
        <IconButton
          size="xs"
          iconSize="sm"
          onClick={() => {
            retryUpload(file.id);
          }}
        >
          <RefreshIcon />
        </IconButton>
      </div>
    );
  } else if (status === 'aborted') {
    statusButton = (
      <AnimatedStatus>
        <WarningIcon className="text-warning" size="md" />
      </AnimatedStatus>
    );
  } else if (status === 'completed') {
    statusButton = (
      <AnimatedStatus>
        <CheckCircleIcon size="md" className="text-positive" />
      </AnimatedStatus>
    );
  } else if (status === 'pending') {
    statusButton = <AnimatedStatus>{abortButton}</AnimatedStatus>;
  } else {
    statusButton = (
      <AnimatedStatus
        onPointerDown={e => {
          if (e.pointerType === 'touch') {
            if (!isHovered) {
              skipNextAbortClick.current = true;
            }
            setIsHovered(true);
          }
        }}
        onPointerEnter={e => {
          if (e.pointerType === 'mouse') {
            setIsHovered(true);
          }
        }}
        onPointerLeave={e => {
          if (e.pointerType === 'mouse') {
            setIsHovered(false);
          }
        }}
      >
        {shouldShowAbortButton ? abortButton : progressButton}
      </AnimatedStatus>
    );
  }

  return <AnimatePresence>{statusButton}</AnimatePresence>;
}

interface AnimatedStatusProps
  extends Omit<
    ComponentPropsWithoutRef<'div'>,
    'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'
  > {
  children: ReactElement;
}
function AnimatedStatus({ children, ...domProps }: AnimatedStatusProps) {
  return (
    <m.div
      {...domProps}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
    >
      {children}
    </m.div>
  );
}